import { useEffect, useRef } from "react";
import * as THREE from "three";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { RootState, AppDispatch } from "store/store";
import { TargetImageData, TransformType } from "utils/interface.utils";
import { setIsTargetImageListItemClicked, setSelectedTransformControl } from "store/reducers/editorSlice";
import { applyTransformCursor } from "components/editor_workspace/scene/transform_cursor.helper";
import { cleanupTransformBox, renderTransformBox } from "components/editor_workspace/scene/transform_box.helper";
import { cleanupObjects, initializeWorkspace, renderTargetImage } from "components/editor_workspace/scene/workspace_scene.helper";
import { dragObjectsAndUpdatePosition } from "components/editor_workspace/scene/object_drag_position.helper";
import { dragObjectsAndUpdateRotation } from "components/editor_workspace/scene/object_drag_rotation.helper";
import { dragObjectAndUpdateScale } from "components/editor_workspace/scene/object_drag_scale.helper";
import { createAndUpdateObjectsOfTargetImage } from "components/editor_workspace/scene/object_sync.helper";
import { DragSystem, ObjectEntry, ObjectScaleSnapshot, RotationSystem, ScaleSystem, WorkspaceContext } from "interfaces/editor_workspace.interface";
import "./editor_workspace.component.scss"; 

const EditorWorkspace = () => {
    const dispatch = useDispatch<AppDispatch>();

    const targetImageDataList = useSelector((state: RootState) => state.editor.targetImageDataList, shallowEqual)
    const { selectedTargetIndex, zoomPercentage, isTargetImageListItemClicked, lastDeletedTargetImageData, selectedTargetImageID, selectedObjectID, currentSelectedPropertyType, selectedTransformControl, isObjectListItemClicked, keepToolForObjectID } = useSelector((state: RootState) => state.editor)

    // Refs
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.OrthographicCamera | null>(null);
    const loaderRef = useRef<THREE.TextureLoader | null>(null);
    const transformBoxRef = useRef<THREE.Group | null>(null);
    const dragSystemRef = useRef<DragSystem | null>(null);
    const rotationSystemRef = useRef<RotationSystem | null>(null);
    const scaleSystemRef = useRef<ScaleSystem | null>(null);
    const targetImageRef = useRef<THREE.Group | null>(null);
    const currentTargetImageSrcRef = useRef<string | null>(null);
    const isTargetImageLoadingRef = useRef<boolean>(false);
    const videoPlayerControlsDimensionsRef = useRef<{ width: number, height: number }>({ width: 28, height: 28 });
    const objectsRef = useRef<Map<string, ObjectEntry>>(new Map());
    const allObjectsPreviousScaleRef = useRef<Array<ObjectScaleSnapshot>>([])
    // Text mesh rebuilds are async (font load via FontFaceObserver) — if updateObjectText fires
    // again for the same object before a prior rebuild's promise settles (e.g. rapid successive
    // font-size changes), both calls read the same stale objectsRef entry and each build their own
    // mesh; whichever resolves last overwrites objectsRef.current, permanently losing the other
    // generation's cleanup() reference — and with it, the only thing that would remove that
    // generation's transform box (a scene-level sibling, not nested in the mesh) from the scene.
    // This counter lets a resolving call detect it's been superseded and dispose itself instead.
    const textUpdateGenerationRef = useRef<Map<string, number>>(new Map());
    
    const selectedTransformControlRef = useRef<TransformType | null>(null);
    const selectedObjectIDRef = useRef<string | null>(null);
    const targetImageDataListRef = useRef<TargetImageData[]>(targetImageDataList);
    // Assigned during render so the mount-once drag systems always read the current target.
    const selectedTargetIndexRef = useRef<number | null>(selectedTargetIndex);
    selectedTargetIndexRef.current = selectedTargetIndex;

    // Everything the extracted workspace functions (src/components/editor_workspace/scene/) used to reach
    // through this component's closure. Rebuilt each render so its Redux snapshot is never staler
    // than the closure it replaces.
    const workspace: WorkspaceContext = {
        refs: {
            rendererRef, canvasRef, sceneRef, cameraRef, loaderRef, transformBoxRef,
            dragSystemRef, rotationSystemRef, scaleSystemRef, targetImageRef,
            currentTargetImageSrcRef, isTargetImageLoadingRef, videoPlayerControlsDimensionsRef,
            objectsRef, allObjectsPreviousScaleRef, textUpdateGenerationRef,
            selectedTransformControlRef, selectedObjectIDRef, targetImageDataListRef, selectedTargetIndexRef,
        },
        dispatch,
        selectedTargetIndex,
        selectedTargetImageID,
        selectedObjectID,
        targetImageDataList,
    };

    // Initial setup effect - runs once on component mount
    useEffect(() => {
        // Initialize the scene once
        initializeWorkspace(workspace);

        // Drag objects and update position
        const dragSystem = dragObjectsAndUpdatePosition(workspace);
        dragSystemRef.current = dragSystem;

        // Drag objects and update rotation
        const rotationSystem = dragObjectsAndUpdateRotation(workspace);
        rotationSystemRef.current = rotationSystem;

        // Drag objects and update scale
        const scaleSystem = dragObjectAndUpdateScale(workspace);
        scaleSystemRef.current = scaleSystem;

        return () => {
            if (dragSystem) dragSystem.cleanup();
            if (rotationSystem) rotationSystem.cleanup();
            // Clean up resources
            cleanupObjects(workspace, true);

            if (rendererRef.current) {
                rendererRef.current.dispose();
                rendererRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        // The pointer systems read the selection through this ref. It used to be synced only when the
        // tool changed, so after a click kept the tool the ref held the previous object and the next
        // click on that object was ignored.
        selectedObjectIDRef.current = selectedObjectID ?? null;

        if (selectedObjectID) {
            if (targetImageRef.current) {
                // Collect first: a transform box is a child of this group, and removing it
                // mid-traverse would corrupt the iteration.
                const withBox: THREE.Object3D[] = [];
                targetImageRef.current.traverse((child) => {
                    if (child.userData.transformBox) withBox.push(child);
                });
                withBox.forEach((child) => cleanupTransformBox(workspace, child));
            }
            if (keepToolForObjectID === selectedObjectID) {
                // Picked by clicking it (canvas or Objects list): keep the tool and show the box right away. The
                // tool effect below will not fire when the tool did not change, so attach it here.
                const mesh = objectsRef.current.get(selectedObjectID)?.mesh;
                if (mesh && selectedTransformControl) renderTransformBox(workspace, mesh);
            } else {
                dispatch(setSelectedTransformControl(null));
            }
        }
    }, [selectedObjectID, isObjectListItemClicked])

    useEffect(() => {
        const loadTargetImageAndObjects = async () => {
            if (isTargetImageListItemClicked) return;

            const selectedData = targetImageDataList[selectedTargetIndex ?? 0];
            if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;

            // The target or its image was taken away (an undone upload): clear it and its objects off the canvas.
            if (!selectedData?.targetImage && targetImageRef.current) {
                cleanupObjects(workspace, true);
                currentTargetImageSrcRef.current = null;
                rendererRef.current.render(sceneRef.current, cameraRef.current);
                return;
            }
            if (!selectedData) return;

            // Wait for target image to load if it exists and is different
            const targetImage = selectedData.targetImage;
            if (targetImage && targetImage !== currentTargetImageSrcRef.current) {
                const targetImageGroup = await renderTargetImage(workspace, targetImage);
                if (targetImageGroup) {
                    createAndUpdateObjectsOfTargetImage(workspace, selectedData);
                }
            } else {
                if (!isTargetImageLoadingRef.current) {
                    createAndUpdateObjectsOfTargetImage(workspace, selectedData);
                }
            }
        };

        loadTargetImageAndObjects();
    }, [targetImageDataList]);

    // Update the previous scale values ref when the propertiesType changes so that it starts tracking the previous values of the current selected object
    useEffect(() => {
        const currentObject = targetImageDataList[selectedTargetIndex ?? 0]?.objects.find((object) => object.id === selectedObjectID);
        const currentObjectProperties = currentObject?.properties;

        if (!currentObject) return;

        // 1. check if the allObjectsPreviousScaleRef has an object with the same ID as the currentObjectProperties.ID
        // 2. if it does, update the previousScale of the object
        // 3. if it doesn't, add the object to the allObjectsPreviousScaleRef
        const objectIndex = allObjectsPreviousScaleRef.current.findIndex((object) => object.ID === currentObject?.id);

        if (objectIndex !== -1) {
            allObjectsPreviousScaleRef.current[objectIndex].scale = { x: currentObjectProperties?.scaleX, y: currentObjectProperties?.scaleY, z: currentObjectProperties?.scaleZ };
        } else {
            allObjectsPreviousScaleRef.current.push({ ID: currentObject?.id, scale: { x: currentObjectProperties?.scaleX, y: currentObjectProperties?.scaleY, z: currentObjectProperties?.scaleZ } });
        }

    }, [selectedObjectID, currentSelectedPropertyType])

    // Clean up the previous target image and its objects when the target image list item is clicked and render the selected target image
    useEffect(() => {
        if (!isTargetImageListItemClicked) return;

        const selectedData = targetImageDataList[selectedTargetIndex ?? 0];
        const targetImage = selectedData?.targetImage;

        if (!selectedData || !sceneRef.current || !cameraRef.current || !rendererRef.current) {
            dispatch(setIsTargetImageListItemClicked(false));
            return;
        };

        // Clean up existing objects when target index changes
        cleanupObjects(workspace);

        currentTargetImageSrcRef.current = targetImage;

        // Render the new target image and update the objects
        if (targetImage) {
            renderTargetImage(workspace, targetImage).then((targetImageGroup) => {
                if (targetImageGroup) {
                    createAndUpdateObjectsOfTargetImage(workspace, selectedData);
                }
                dispatch(setIsTargetImageListItemClicked(false));
            });
        } else {
            dispatch(setIsTargetImageListItemClicked(false));
        }
    }, [isTargetImageListItemClicked]);

    // Update the zoom percentage
    useEffect(() => {
        if (cameraRef.current) {
            // Convert percentage to scale
            const newScale = Math.max(0.1, Math.min(2, 2 - (zoomPercentage / 100)));
            // Adjust camera scale directly
            cameraRef.current.scale.set(newScale, newScale, newScale);
            // Update camera projection
            cameraRef.current.updateProjectionMatrix();
        }
    }, [zoomPercentage]);

    useEffect(() => {
        if (lastDeletedTargetImageData) {
            cleanupObjects(workspace);
        }
    }, [lastDeletedTargetImageData])

    useEffect(() => {
        // Keep the ref in sync with the Redux value unconditionally — including when it resets
        // to null (e.g. after a delete/reselect) — so code elsewhere that reads the ref outside
        // of an active drag (like updateObjectText's box-reattach check) doesn't see a stale,
        // truthy tool value left over from a previous selection.
        selectedTransformControlRef.current = selectedTransformControl;
        selectedObjectIDRef.current = selectedObjectID ?? null;

        // The canvas cursor follows the selected tool straight away, without waiting for a mouse move.
        applyTransformCursor(canvasRef.current, { tool: selectedTransformControl, corner: null, dragging: false });

        if (selectedObjectID) {
            const objectData = objectsRef.current.get(selectedObjectID);
            if (objectData && objectData.mesh) {
                if (selectedTransformControl) {
                    if (!objectData.mesh.userData.transformBox) {
                        renderTransformBox(workspace, objectData.mesh);
                    }
                } else if (objectData.mesh.userData.transformBox) {
                    cleanupTransformBox(workspace, objectData.mesh);
                }
            }
        }
    }, [selectedTransformControl])

    return (
        <div className="workspace">
            <canvas ref={canvasRef} className="workspace_canvas"></canvas>
        </div>
    )
}

export default EditorWorkspace;